import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import * as LucideIcons from "lucide-react";
import { MdHistory, MdOutlineClose, MdOutlineRestore } from "react-icons/md";
import { BsThreeDotsVertical } from "react-icons/bs";
import { IoWarningOutline, IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import SAPagination from "../common/SAPagination";
import ProjectVersionService from "../../services/api/projectVersion";
import { formatDate } from "../../settings/shared/commonMethods";
import { websocketModuleType } from "../../settings/shared/constants";

const CHECKPOINTS_PAGE_SIZE = 10;
const RESTORE_STATE_KEY_PREFIX = "checkpoint-restore:";

const getCheckpointId = (checkpoint) =>
  checkpoint?.id ?? checkpoint?.checkpoint_id ?? checkpoint?._id ?? null;

const getRestoreStateKey = (projectId) => `${RESTORE_STATE_KEY_PREFIX}${projectId}`;

const loadRestoreState = (projectId) => {
  if (!projectId || typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(getRestoreStateKey(projectId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveRestoreState = (projectId, state) => {
  if (!projectId || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(getRestoreStateKey(projectId), JSON.stringify(state));
  } catch {
    // ignore quota / private-mode failures
  }
};

const clearRestoreState = (projectId) => {
  if (!projectId || typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.removeItem(getRestoreStateKey(projectId));
  } catch {
    // ignore
  }
};

const parseWsDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === "object") return detail;
  if (typeof detail === "string") {
    try {
      return JSON.parse(detail);
    } catch {
      return null;
    }
  }
  return null;
};

const getRestoreEventMessage = (ninfo, message) =>
  ninfo?.message ||
  ninfo?.msg ||
  message?.message ||
  message?.msg ||
  null;

const isCheckpointRestoreEvent = (message) => {
  if (!message) return false;
  const moduleType =
    message?.ninfo?.moduleType ||
    message?.moduleType ||
    null;
  return (
    moduleType === websocketModuleType.CHECKPOINT_RESTORE ||
    moduleType === "CHECKPOINT_RESTORE"
  );
};

const Styles = {
  panel: (open) => ({
    position: "absolute",
    top: 0,
    right: 0,
    width: 520,
    maxWidth: "100%",
    height: "100%",
    background: "#fff",
    borderLeft: "0.5px solid rgba(0,0,0,0.12)",
    zIndex: 9999,
    display: "flex",
    flexDirection: "column",
    transform: open ? "translateX(0)" : "translateX(100%)",
    transition: "transform 0.22s ease",
  }),
  panelHeader: {
    padding: "20px 24px",
    borderBottom: "0.5px solid rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexShrink: 0,
  },
  panelTitle: { fontSize: 16, fontWeight: 600 },
  panelBody: { padding: 24, flex: 1, overflowY: "auto" },
};

const ModalStyles = {
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.45)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
  },
  box: {
    position: "relative",
    background: "#fff",
    borderRadius: "14px",
    padding: "28px",
    width: "380px",
    maxWidth: "90vw",
    textAlign: "center",
    boxShadow: "0 20px 40px rgba(0,0,0,0.18)",
  },
  closeX: {
    position: "absolute",
    top: "12px",
    right: "12px",
    border: "1px solid #e5e7eb",
    borderRadius: "6px",
    background: "#fff",
    cursor: "pointer",
    padding: "5px",
    display: "flex",
  },
  title: { fontSize: "17px", fontWeight: 700, color: "#111827", margin: "0 0 6px" },
  subtitle: { fontSize: "13px", color: "#6b7280", margin: "0 0 12px", lineHeight: 1.5 },
  previewCard: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "12px",
  },
  warningBanner: {
    background: "#fff7ed",
    color: "#c2410c",
    fontSize: "12px",
    fontWeight: 500,
    borderRadius: "8px",
    padding: "8px 10px",
    textAlign: "left",
  },
  cancelBtn: {
    flex: 1, padding: "10px", borderRadius: "8px", border: "1px solid #d1d5db",
    background: "#fff", color: "#374151", fontSize: "13px", fontWeight: 600, cursor: "pointer",
  },
  primaryBtn: {
    flex: 1, padding: "10px", borderRadius: "8px", border: "none",
    background: "#4f46e5", color: "#fff", fontSize: "13px", fontWeight: 600, cursor: "pointer",
  },
  progressTrack: {
    width: "100%", height: "8px", background: "#e5e7eb", borderRadius: "999px", overflow: "hidden",
  },
};

const CheckpointDrawer = ({ open, onClose, projectId }) => {
  const [openMenu, setOpenMenu] = useState(null);
  const [versionData, setVersionData] = useState([]);
  const [checkpointPage, setCheckpointPage] = useState(0);
  const [expandedSummary, setExpandedSummary] = useState({});
  const [restoreModal, setRestoreModal] = useState({
    step: null,
    checkpoint: null,
    errorMessage: null,
    statusMessage: null,
  });
  const [restoreProgress, setRestoreProgress] = useState(0);
  const projectIdRef = useRef(projectId);
  projectIdRef.current = projectId;

  const persistRestoreState = (nextModal, progress = restoreProgress) => {
    if (!projectId) return;
    if (!nextModal?.step || nextModal.step === "confirm") {
      clearRestoreState(projectId);
      return;
    }
    saveRestoreState(projectId, {
      step: nextModal.step,
      checkpoint: nextModal.checkpoint,
      errorMessage: nextModal.errorMessage || null,
      statusMessage: nextModal.statusMessage || null,
      progress,
      updatedAt: Date.now(),
    });
  };

  const applyRestoreEvent = (rawMessage) => {
    const message = parseWsDetail(rawMessage);
    if (!isCheckpointRestoreEvent(message)) return;

    const ninfo = message?.ninfo || message;
    if (!ninfo) return;

    const currentProjectId = projectIdRef.current;
    if (
      currentProjectId &&
      ninfo.projectId &&
      String(ninfo.projectId) !== String(currentProjectId)
    ) {
      return;
    }

    const eventMessage = getRestoreEventMessage(ninfo, message);
    const nextProgress =
      typeof ninfo.progress === "number"
        ? Math.min(100, Math.max(0, Math.round(ninfo.progress)))
        : null;

    if (nextProgress != null) {
      setRestoreProgress(nextProgress);
    }

    const status = String(ninfo.status || "").toUpperCase();

    if (status === "RESTORED") {
      setRestoreProgress(100);
      setRestoreModal((prev) => {
        const next = {
          ...prev,
          step: "success",
          errorMessage: null,
          statusMessage: eventMessage || prev.statusMessage,
          checkpoint: prev.checkpoint,
        };
        persistRestoreState(next, 100);
        return next;
      });
      return;
    }

    if (status === "FAILED" || status === "ERROR") {
      setRestoreModal((prev) => {
        const next = {
          ...prev,
          step: "error",
          errorMessage:
            eventMessage ||
            "Unable to restore checkpoint at this time. Please try again later.",
          statusMessage: eventMessage || prev.statusMessage,
        };
        persistRestoreState(next, nextProgress != null ? nextProgress : restoreProgress);
        return next;
      });
      return;
    }

    // In-progress updates (progress / message) while still restoring
    setRestoreModal((prev) => {
      const next = {
        ...prev,
        step: prev.step === "success" || prev.step === "error" ? prev.step : "restoring",
        statusMessage: eventMessage || prev.statusMessage,
      };
      persistRestoreState(next, nextProgress != null ? nextProgress : restoreProgress);
      return next;
    });
  };

  const fetchProjectVersions = async () => {
    try {
      setCheckpointPage(0);
      const response = await ProjectVersionService.getProjectVersion(projectId);
      setVersionData(response?.versionDTOS?.content || []);
    } catch (error) {
      console.error("Error fetching project versions", error);
    }
  };

  // Resume in-progress / completed restore UI after refresh or remount
  useEffect(() => {
    if (!projectId) return;
    const saved = loadRestoreState(projectId);
    if (!saved?.step) return;
    if (saved.step === "confirm") {
      clearRestoreState(projectId);
      return;
    }
    setRestoreModal({
      step: saved.step,
      checkpoint: saved.checkpoint || null,
      errorMessage: saved.errorMessage || null,
      statusMessage: saved.statusMessage || null,
    });
    setRestoreProgress(typeof saved.progress === "number" ? saved.progress : 0);
  }, [projectId]);

  useEffect(() => {
    if (open && projectId) {
      fetchProjectVersions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  useEffect(() => {
    if (restoreModal.step) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [restoreModal.step]);

  useEffect(() => {
    const handleCheckpointRestoreMessage = (event) => {
      applyRestoreEvent(event?.detail);
    };

    // Also listen to the raw websocket bus in case layout routing misses NOTIFICATION events
    const handleRawMessageReceived = (event) => {
      applyRestoreEvent(event?.detail);
    };

    window.addEventListener("checkpointRestoreMessageReceived", handleCheckpointRestoreMessage);
    window.addEventListener("messageReceived", handleRawMessageReceived);
    return () => {
      window.removeEventListener("checkpointRestoreMessageReceived", handleCheckpointRestoreMessage);
      window.removeEventListener("messageReceived", handleRawMessageReceived);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const handleRestoreClick = (checkpoint) => {
    setOpenMenu(null);
    setRestoreModal({ step: "confirm", checkpoint, errorMessage: null, statusMessage: null });
  };

  const handleCloseRestoreModal = () => {
    clearRestoreState(projectId);
    setRestoreModal({ step: null, checkpoint: null, errorMessage: null, statusMessage: null });
    setRestoreProgress(0);
  };

  const handleConfirmRestore = async () => {
    const checkpointId = getCheckpointId(restoreModal.checkpoint);
    if (!checkpointId) {
      const next = {
        ...restoreModal,
        step: "error",
        errorMessage: "Checkpoint id is missing. Unable to restore.",
      };
      setRestoreModal(next);
      persistRestoreState(next, 0);
      return;
    }

    const next = {
      ...restoreModal,
      step: "restoring",
      errorMessage: null,
      statusMessage: null,
    };
    setRestoreModal(next);
    setRestoreProgress(0);
    persistRestoreState(next, 0);

    try {
      await ProjectVersionService.restoreCheckpoint(checkpointId);
      // Stay on "restoring" until CHECKPOINT_RESTORE websocket confirms completion.
    } catch (err) {
      console.error("Error restoring checkpoint", err);
      const errorNext = {
        ...next,
        step: "error",
        errorMessage:
          err?.response?.data?.errorMessage ||
          err?.message ||
          "Unable to restore checkpoint at this time. Please try again later.",
      };
      setRestoreModal(errorNext);
      persistRestoreState(errorNext, 0);
    }
  };

  const handleGoToProject = () => {
    handleCloseRestoreModal();
    // Refresh the checkpoint list so the restored one shows as Current
    if (open) fetchProjectVersions();
  };

  const showRestoreModals = Boolean(restoreModal.step);

  if (!open && !showRestoreModals) return null;

  return (
    <>
      {/* Drawer panel — only when explicitly opened */}
      {open && (
      <div style={Styles.panel(open)}>
        <div style={{ ...Styles.panelHeader, alignItems: "flex-start" }}>
          <div style={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "flex-start" }}>
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "#6d28d9",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <MdHistory size={18} color="#fff" />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <span style={Styles.panelTitle}>Check Points</span>
              <span style={{ fontSize: "12.5px", color: "#6b7280" }}>
                Browse and restore the project to a previous checkpoint.
              </span>
            </div>
          </div>
          <button
            style={{
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              padding: "6px",
              background: "#fff",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#6b7280",
              flexShrink: 0,
            }}
            onClick={onClose}
          >
            <MdOutlineClose size={14} />
          </button>
        </div>

        <div style={Styles.panelBody}>
          {versionData?.length > 0 ? (
            <div className="timeline-container">
              {versionData
                .slice(
                  checkpointPage * CHECKPOINTS_PAGE_SIZE,
                  (checkpointPage + 1) * CHECKPOINTS_PAGE_SIZE
                )
                .map((checkpoint, i) => {
                  const index = checkpointPage * CHECKPOINTS_PAGE_SIZE + i;
                  return (
                    <div className="timeline-item" key={index}>
                      <div
                        className="timeline-dot"
                        style={{
                          backgroundColor: checkpoint?.latest ? "#22c55e" : "#fff",
                          border: checkpoint?.latest
                            ? "2px solid #22c55e"
                            : "2px solid #d1d5db",
                        }}
                      />

                      <div
                        className="checkpoint-card"
                        style={
                          checkpoint?.latest
                            ? {
                                background: "#f0fdf4",
                                border: "1px solid #bbf7d0",
                              }
                            : undefined
                        }
                      >
                        <div className="checkpoint-card-top">
                          <div className="version-row">
                            <span className="checkpoint-version">
                              {checkpoint?.checkpoint_name}
                            </span>
                            {checkpoint?.latest && (
                              <span className="current-badge">Current</span>
                            )}
                          </div>

                          {!checkpoint?.latest && (
                            <div
                              style={{
                                position: "relative",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <button
                                style={{
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  padding: "4px",
                                  borderRadius: "4px",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenMenu(openMenu === index ? null : index);
                                }}
                              >
                                <BsThreeDotsVertical size={14} color="#6b7280" />
                              </button>

                              {openMenu === index && (
                                <>
                                  <div
                                    style={{ position: "fixed", inset: 0, zIndex: 10 }}
                                    onClick={() => setOpenMenu(null)}
                                  />
                                  <button
                                    style={{
                                      position: "absolute",
                                      top: "50%",
                                      right: "26px",
                                      transform: "translateY(-50%)",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "6px",
                                      padding: "8px 14px",
                                      borderRadius: "999px",
                                      border: "1px solid #e5e7eb",
                                      background: "#fff",
                                      boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                                      cursor: "pointer",
                                      fontSize: "13px",
                                      fontWeight: 600,
                                      color: "#4f46e5",
                                      whiteSpace: "nowrap",
                                      zIndex: 20,
                                    }}
                                    onClick={() => handleRestoreClick(checkpoint)}
                                  >
                                    <MdOutlineRestore size={16} />
                                    Restore
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                        <div style={{ position: "relative" }}>
                          <p
                            className="checkpoint-summary"
                            style={{
                              display: expandedSummary[index] ? "block" : "-webkit-box",
                              WebkitLineClamp: expandedSummary[index] ? "unset" : 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              marginBottom: "2px",
                            }}
                          >
                            {checkpoint?.completed_change_summary}
                          </p>

                          {checkpoint?.completed_change_summary?.length > 100 && (
                            <span
                              onClick={() =>
                                setExpandedSummary((prev) => ({
                                  ...prev,
                                  [index]: !prev[index],
                                }))
                              }
                              style={{
                                display: "inline-block",
                                color: "#2563eb",
                                cursor: "pointer",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              {expandedSummary[index] ? "Show less" : "Show more"}
                            </span>
                          )}
                        </div>
                        <div className="checkpoint-footer">
                          <div className="checkpoint-time">
                            🕒 {formatDate(checkpoint?.updatedDate)}
                          </div>
                          <div className="checkpoint-updated-by">System</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "60px 20px",
                color: "#6b7280",
              }}
            >
              <LucideIcons.GitBranch size={28} style={{ color: "#9ca3af" }} />
              <span style={{ fontSize: "14px", fontWeight: 500, textAlign: "center" }}>
                No checkpoints found
              </span>
            </div>
          )}
        </div>

        <div
          style={{
            padding: "12px 24px",
            borderTop: "0.5px solid rgba(0,0,0,0.08)",
            flexShrink: 0,
          }}
        >
          <SAPagination
            currentPage={checkpointPage}
            totalPages={Math.ceil(versionData.length / CHECKPOINTS_PAGE_SIZE)}
            totalItems={versionData.length}
            itemsPerPage={CHECKPOINTS_PAGE_SIZE}
            onPageChange={setCheckpointPage}
            showGoToPage={false}
            showPageInfo={true}
            size="sm"
          />
        </div>
      </div>
      )}

      {/* Confirm Restore Modal */}
      {restoreModal.step === "confirm" && (
        <div style={ModalStyles.overlay}>
          <div style={ModalStyles.box}>
            <button style={ModalStyles.closeX} onClick={handleCloseRestoreModal}>
              <MdOutlineClose size={16} />
            </button>

            <IoWarningOutline size={40} color="#f97316" style={{ marginBottom: "12px" }} />

            <h3 style={ModalStyles.title}>Restore this checkpoint?</h3>
            <p style={ModalStyles.subtitle}>
              You are about to restore the project to the selected checkpoint.
            </p>
            <p style={{ ...ModalStyles.subtitle, color: "#dc2626", marginTop: "-8px" }}>
              The current project changes may be replaced.
            </p>

            <div style={ModalStyles.previewCard}>
              <MdOutlineRestore size={16} color="#6b7280" />
              <div style={{ textAlign: "left" }}>
                <div style={{ fontWeight: 600, fontSize: "13px", color: "#111827" }}>
                  {restoreModal.checkpoint?.checkpoint_name}
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280" }}>
                  {formatDate(restoreModal.checkpoint?.updatedDate)}
                </div>
              </div>
            </div>

            <div style={ModalStyles.warningBanner}>
              ⚠️ Any unsaved changes could be lost.
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "16px" }}>
              <button
                style={ModalStyles.cancelBtn}
                onClick={handleCloseRestoreModal}
                disabled={restoreModal.step === "restoring"}
              >
                Cancel
              </button>
              <button
                style={ModalStyles.primaryBtn}
                onClick={handleConfirmRestore}
                disabled={restoreModal.step === "restoring"}
              >
                Restore
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restoring Modal */}
      {restoreModal.step === "restoring" && (
        <div style={ModalStyles.overlay}>
          <div style={ModalStyles.box}>
            <div className="checkpoint-spinner" style={{ margin: "0 auto 16px" }} />
            <h3 style={ModalStyles.title}>Restoring checkpoint…</h3>
            <p style={ModalStyles.subtitle}>
              {restoreModal.statusMessage || "Please wait while we restore the project."}
            </p>

            <div style={ModalStyles.progressTrack}>
              <div
                style={{
                  width: `${restoreProgress}%`,
                  height: "100%",
                  background: "#4f46e5",
                  borderRadius: "999px",
                  transition: "width 0.3s ease",
                }}
              />
            </div>
            <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px" }}>
              {restoreProgress}%
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {restoreModal.step === "success" && (
        <div style={ModalStyles.overlay}>
          <div style={ModalStyles.box}>
            <button style={ModalStyles.closeX} onClick={handleGoToProject}>
              <MdOutlineClose size={16} />
            </button>

            <IoCheckmarkCircle size={48} color="#22c55e" style={{ marginBottom: "12px" }} />
            <h3 style={ModalStyles.title}>Restored successfully!</h3>
            <p style={ModalStyles.subtitle}>
              {restoreModal.statusMessage || (
                <>
                  The project has been restored to{" "}
                  <strong style={{ color: "#111827" }}>
                    {restoreModal.checkpoint?.checkpoint_name}
                  </strong>
                  .
                </>
              )}
            </p>

            <div style={{ textAlign: "left", margin: "0 0 16px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151", marginBottom: "6px" }}>
                <IoCheckmarkCircle size={14} color="#22c55e" />
                Project files have been refreshed.
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: "#374151" }}>
                <IoCheckmarkCircle size={14} color="#22c55e" />
                A new checkpoint has been created.
              </div>
            </div>

            <button
              style={{ ...ModalStyles.primaryBtn, width: "100%" }}
              onClick={handleGoToProject}
            >
              View Project
            </button>
          </div>
        </div>
      )}

      {/* Restore Failed Modal */}
      {restoreModal.step === "error" && (
        <div style={ModalStyles.overlay}>
          <div style={ModalStyles.box}>
            <button style={ModalStyles.closeX} onClick={handleCloseRestoreModal}>
              <MdOutlineClose size={16} />
            </button>

            <IoCloseCircle size={48} color="#ef4444" style={{ marginBottom: "12px" }} />
            <h3 style={ModalStyles.title}>Restore failed</h3>
            <p style={ModalStyles.subtitle}>
              We couldn't restore the selected checkpoint. Please try again.
            </p>

            <div
              style={{
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "10px 12px",
                textAlign: "left",
                marginBottom: "16px",
              }}
            >
              <div style={{ fontSize: "12px", fontWeight: 600, color: "#b91c1c", marginBottom: "2px" }}>
                Error Details
              </div>
              <div style={{ fontSize: "12px", color: "#b91c1c" }}>
                {restoreModal.errorMessage ||
                  "Unable to restore checkpoint at this time. Please try again later."}
              </div>
            </div>

            <button
              style={{ ...ModalStyles.cancelBtn, width: "100%" }}
              onClick={handleCloseRestoreModal}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
};

CheckpointDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default CheckpointDrawer;
